import {Color} from '@benchling/nucleus-ui';
import {StyleSheet, css} from 'aphrodite';
import React, {useState} from 'react';
import {useSelector} from 'react-redux';

import {assert} from 'Asserts';
import {ComplexPolymerConnectionType, HELMPolymerType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as ComplexPolymerActions from 'modbio/complex-polymers/ComplexPolymerActions';
import {
  ComplexPolymerConnection,
  ComplexPolymerType,
  ConjugateGraphQL,
  ConnectionGraphQL,
  OLIGO_DUPLEX_REVERSE_STRAND_REF,
  OligoDuplexOligoStrandGraphQL,
  OligoGraphQL,
  ResolvedAttachmentGraphQL,
  ResolvedAttachmentInput,
  STRAND_TYPE_TO_DISPLAY_NAME,
  SequenceEntityGraphQL,
  SimplePolymerGraphQL,
  StrandType,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {
  getConnectionInputs,
  getExistingResolvedAttachmentInputs,
  getPolymerTypeFromRef,
  getResolvedAttachmentsForOligoDuplex,
} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {BenchlingState} from 'redux-stores/reduceApp';
import getTestProps from 'test/lib/cypress/getTestProps';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BioentityChip} from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_SEMIDARK} from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import legacyRunModal from 'ui-library/legacyRunModal';
import {withLoadingScreen} from 'util/Promises';

import {generateBackbonePolymerStructureGraphNodes} from '../GeneratePolymerStructureGraphUtil';
import {PolymerStructureGraph} from '../PolymerStructureGraphConstants';
import ComplexPolymerAddConjugateMenu from './ComplexPolymerAddConjugateMenu';
import {
  getComplexPolymerConnectionFromConjugateConnection,
  getResolvedAttachmentsInputsForSubmit,
  isCompleteConjugateConnection,
} from './ComplexPolymerAttachConjugateModalUtil';
import {
  ChemicalStructureImageData,
  ConjugateConnection,
  ConjugateConnectionMap,
  getChemicalStructureImage,
  getIconId,
  makeConjugateConnections,
} from './ComplexPolymerComponentsSummaryUtil';
import {removeConjugate} from './ComplexPolymerRemoveConjugateUtil';
import ComplexPolymerRemoveConjugateWarningModal, {
  ComplexPolymerRemoveConjugateWarningModalProps,
} from './ComplexPolymerRemoveConjugateWarningModal';
import ComplexPolymerSwapOligoMenu from './ComplexPolymerSwapOligoMenu';
import OligoDuplexOffsetField from './OligoDuplexOffsetField';
import ResolvedAttachmentSummary from './ResolvedAttachmentSummary';

// Collection of data required by subcomponents to display info and make updates to the complex polymer.
export interface ComplexPolymerUpdateData {
  complexPolymerId: string;
  versionId: string;
  complexPolymerName: string;
  simplePolymers: Array<SimplePolymerGraphQL>;
  connections: Array<ConnectionGraphQL>;
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>;
  complexPolymerType: ComplexPolymerType;
  offset?: number;
}

interface OligoDuplexStrandSummaryProps {
  complexPolymerId: string;
  versionId: string;
  isEditable: boolean;
  simplePolymers: Array<SimplePolymerGraphQL>;
  connections: Array<ConnectionGraphQL>;
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>;
  oligo: OligoGraphQL;
  offset: number | null;
  isForward: boolean;
  structureGraph: PolymerStructureGraph | null;
}

function OligoDuplexStrandSummary({
  complexPolymerId,
  versionId,
  isEditable,
  simplePolymers,
  connections,
  resolvedAttachments,
  oligo,
  offset,
  isForward,
  structureGraph,
}: OligoDuplexStrandSummaryProps): JSX.Element {
  return (
    <div
      {...getTestProps({
        component: 'ComplexPolymerComponentsSummary',
        element: `${isForward ? 'forward' : 'reverse'}Strand`,
      })}
    >
      <div>
        <FieldLabel
          label={`${STRAND_TYPE_TO_DISPLAY_NAME[isForward ? StrandType.FORWARD : StrandType.REVERSE]} strand`}
        />
        {isEditable && (
          <ComplexPolymerSwapOligoMenu
            complexPolymerId={complexPolymerId}
            complexPolymerVersionId={versionId}
            complexPolymerType={ComplexPolymerType.OLIGO_DUPLEX}
            simplePolymers={simplePolymers}
            connections={connections}
            resolvedAttachments={resolvedAttachments}
            oligo={oligo}
            offset={offset}
            isForward={isForward}
            structureGraph={structureGraph}
          />
        )}
      </div>
      <div className={css(styles.strandChip)}>
        <BioentityChip
          id={isInaccessible(oligo.entity) ? null : oligo.entity.id}
          size={ChipSize.MINI}
          validationExtras={null}
          shouldEllipsize={true}
        />
      </div>
    </div>
  );
}

interface ChemicalStructureImageProps {
  chemicalStructureImage: ChemicalStructureImageData;
}

function ChemicalStructureImage({chemicalStructureImage}: ChemicalStructureImageProps): JSX.Element {
  return (
    <>
      {chemicalStructureImage.base64Svg != null && (
        <img
          className={css(styles.chemicalStructureImageNoRelativePosition)}
          src={`data:image/svg+xml;base64,${chemicalStructureImage.base64Svg}`}
        />
      )}
      {chemicalStructureImage.message != null && <div>{chemicalStructureImage.message}</div>}
    </>
  );
}

interface ConjugateSummaryProps {
  complexPolymerId: string;
  complexPolymerVersionId: string;
  complexPolymerType: ComplexPolymerType;
  isEditable: boolean;
  conjugateConnection: ConjugateConnection;
  allConjugateConnections: ConjugateConnectionMap;
  simplePolymers: Array<SimplePolymerGraphQL>;
  attachedConjugates: Array<ConjugateGraphQL>;
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>;
  complexPolymerName: string;
  structureGraph: PolymerStructureGraph | null;
  offset?: number;
  onAddConnection: (
    conjugateConnection: ConjugateConnection,
    attachmentMolfile: string | null
  ) => Promise<void>;
}

export function ConjugateSummary({
  complexPolymerId,
  complexPolymerVersionId,
  complexPolymerType,
  isEditable,
  conjugateConnection,
  allConjugateConnections,
  simplePolymers,
  attachedConjugates,
  resolvedAttachments,
  complexPolymerName,
  structureGraph,
  offset,
  onAddConnection,
}: ConjugateSummaryProps): JSX.Element {
  // If a connection exists, linkedEntityHelmReference will be non-null on conjugateConnection.
  const [hoverConjugateSummary, setHoverConjugateSummary] = useState(false);

  const conjugatePolymerType = getPolymerTypeFromRef(conjugateConnection.helmReference);

  const conjugateIcon =
    conjugatePolymerType === HELMPolymerType.RNA ? (
      <BenchlingIcon
        className={css(styles.conjugateIcon)}
        icon={getIconId(conjugateConnection.entityId!, simplePolymers) as BenchlingIconName}
      />
    ) : null;

  const chemicalStructureImage =
    conjugatePolymerType === HELMPolymerType.CHEM ? (
      <ChemicalStructureImage
        chemicalStructureImage={getChemicalStructureImage(conjugateConnection.id, simplePolymers)}
      />
    ) : null;

  return (
    <div
      onMouseEnter={() => {
        setHoverConjugateSummary(true);
      }}
      onMouseLeave={() => {
        setHoverConjugateSummary(false);
      }}
      className={
        isEditable ? css(styles.conjugateSummary, styles.conjugateSummaryHover) : css(styles.conjugateSummary)
      }
      {...getTestProps({
        component: 'ConjugateSummary',
        element: 'root',
        key: conjugateConnection.helmReference,
      })}
    >
      <div className={css(styles.chipAndAttachmentInfo)}>
        <BioentityChip
          className={css(styles.conjugateChip)}
          id={conjugateConnection.entityId}
          size={ChipSize.MINI}
          validationExtras={null}
        />
      </div>
      <div className={css(styles.conjugateImage)}>
        {conjugateIcon}
        {chemicalStructureImage}
        {hoverConjugateSummary && isEditable && (
          <div className={css(styles.singleAttachmentIcon)}>
            <Button
              className={css(styles.deletionButton)}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.MINI}
              icon="trash-can"
              dataTestAttrs={{component: 'ConjugateSummary', element: 'deleteButton'}}
              onClick={() =>
                legacyRunModal<ComplexPolymerRemoveConjugateWarningModalProps>(
                  ComplexPolymerRemoveConjugateWarningModal,
                  {
                    entityId: conjugateConnection.entityId!,
                    complexPolymerName,
                    onRemoveConjugate: async () => {
                      await removeConjugate(
                        complexPolymerId,
                        complexPolymerVersionId,
                        complexPolymerType,
                        simplePolymers,
                        attachedConjugates,
                        resolvedAttachments,
                        conjugateConnection.entityId!,
                        conjugateConnection.helmReference,
                        structureGraph,
                        offset
                      );
                    },
                  }
                )
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}

interface SectionHeaderProps {
  title: string;
  count?: number;
  actionButton?: JSX.Element;
}

function SectionHeader({title, count, actionButton}: SectionHeaderProps): JSX.Element {
  return (
    <div className={css(styles.entityHeader)}>
      <Row>
        <FieldLabel className={css(styles.sectionHeader)} label={count ? `${title} (${count})` : title} />
        {actionButton}
      </Row>
    </div>
  );
}

function getUnusedIdsFromResolvedAttachments(
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>,
  conjugateConnectionMap: ConjugateConnectionMap
): Array<string> {
  // Get all conjugates that are not attached in the complex polymer and do not have an associated resolved
  // attachment.
  return Object.keys(conjugateConnectionMap).filter(
    (helmReference) =>
      conjugateConnectionMap[helmReference].linkedEntityHelmReference == null ||
      !resolvedAttachments.some(
        (resolvedAttachment) =>
          resolvedAttachment.polymers.map((polymer) => polymer.helmReference).includes(helmReference) &&
          resolvedAttachment.molfile != null
      )
  );
}

interface ConjugatesSummaryProps {
  complexPolymerId: string;
  complexPolymerVersionId: string;
  complexPolymerType: ComplexPolymerType;
  isEditable: boolean;
  attachedConjugates: Array<ConjugateGraphQL>;
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>;
  simplePolymers: Array<SimplePolymerGraphQL>;
  conjugateConnectionMap: ConjugateConnectionMap;
  complexPolymerName: string;
  structureGraph: PolymerStructureGraph | null;
  onAddConnection: (
    conjugateConnection: ConjugateConnection,
    attachmentMolfile: string | null
  ) => Promise<void>;
  offset?: number;
}

export function ConjugatesSummary({
  complexPolymerId,
  complexPolymerVersionId,
  complexPolymerType,
  isEditable,
  attachedConjugates,
  resolvedAttachments,
  simplePolymers,
  conjugateConnectionMap,
  complexPolymerName,
  structureGraph,
  onAddConnection,
  offset,
}: ConjugatesSummaryProps): JSX.Element {
  const conjugateIdsNotInResolvedAttachments = getUnusedIdsFromResolvedAttachments(
    resolvedAttachments,
    conjugateConnectionMap
  );
  return (
    <div
      className={css(styles.conjugatesSummary)}
      {...getTestProps({component: 'ComplexPolymerComponentsSummary', element: 'conjugates'})}
    >
      {conjugateIdsNotInResolvedAttachments.map((conjugateHelmReference) => (
        <div key={conjugateHelmReference}>
          <ConjugateSummary
            complexPolymerId={complexPolymerId}
            complexPolymerVersionId={complexPolymerVersionId}
            complexPolymerType={complexPolymerType}
            isEditable={isEditable}
            conjugateConnection={conjugateConnectionMap[conjugateHelmReference]}
            allConjugateConnections={conjugateConnectionMap}
            simplePolymers={simplePolymers}
            attachedConjugates={attachedConjugates}
            resolvedAttachments={resolvedAttachments}
            complexPolymerName={complexPolymerName}
            onAddConnection={onAddConnection}
            structureGraph={structureGraph}
            offset={offset}
          />
        </div>
      ))}
    </div>
  );
}

function canEditComplexPolymer(isEditable: boolean, simplePolymers: Array<SimplePolymerGraphQL>): boolean {
  const inaccessibleSimplePolymers = simplePolymers.filter((simplePolymer) =>
    isInaccessible(simplePolymer.entity)
  );
  return isEditable && inaccessibleSimplePolymers.length === 0;
}

export function getExistingConnectionInputs(
  currentConjugateId: string,
  conjugateConnectionMap: ConjugateConnectionMap
): Array<ComplexPolymerConnection> {
  const maybeConnectionInputs = Object.values(conjugateConnectionMap).map((conjugateConnection) => {
    // Skip the current conjugate and conjugates that are not attached
    if (
      conjugateConnection.id === currentConjugateId ||
      !isCompleteConjugateConnection(conjugateConnection)
    ) {
      return null;
    }
    return getComplexPolymerConnectionFromConjugateConnection(conjugateConnection);
  });

  return maybeConnectionInputs.filter((connectionInput) => connectionInput != null);
}

export async function updateComplexPolymer(
  complexPolymerType: ComplexPolymerType,
  complexPolymerId: string,
  complexPolymerVersionId: string,
  simplePolymers: Array<SimplePolymerGraphQL>,
  connectionInputs: Array<ComplexPolymerConnection>,
  resolvedAttachmentInputs: Array<ResolvedAttachmentInput>,
  offset: number | null,
  structureGraph: PolymerStructureGraph | null = null
): Promise<void> {
  // Get all entity IDs.
  const entityIds = simplePolymers.map((simplePolymer) => {
    assert(!isInaccessible(simplePolymer.entity), 'Expected entity to be accessible');
    return simplePolymer.entity.id;
  });
  // Update entity associations.
  const entityAssociations: {[apiId: string]: Array<string>} = {};
  simplePolymers.map((simplePolymer) => {
    assert(!isInaccessible(simplePolymer.entity), 'Expected entity to be accessible');
    if (entityAssociations.hasOwnProperty(simplePolymer.entity.id)) {
      entityAssociations[simplePolymer.entity.id].push(simplePolymer.helmReference);
    } else {
      entityAssociations[simplePolymer.entity.id] = [simplePolymer.helmReference];
    }
    return null;
  });

  await ComplexPolymerActions.updateComplexPolymer(
    complexPolymerId,
    complexPolymerVersionId,
    entityIds,
    connectionInputs,
    resolvedAttachmentInputs,
    entityAssociations,
    complexPolymerType,
    offset ?? undefined,
    structureGraph ?? undefined
  );
}

export async function onAddConnection(
  complexPolymerType: ComplexPolymerType,
  complexPolymerId: string,
  complexPolymerVersionId: string,
  simplePolymers: Array<SimplePolymerGraphQL>,
  conjugateConnection: ConjugateConnection,
  conjugateConnectionMap: ConjugateConnectionMap,
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>,
  offset: number | null,
  attachmentMolfile: string | null,
  structureGraph: PolymerStructureGraph | null = null
): Promise<void> {
  assert(isCompleteConjugateConnection(conjugateConnection), 'Expected connection to be connected.');
  // Get existing connections which are not stale.
  const connectionInputs = getExistingConnectionInputs(conjugateConnection.id, conjugateConnectionMap);

  // Add new connection to the existing connections
  const newConnection = getComplexPolymerConnectionFromConjugateConnection(conjugateConnection);
  connectionInputs.push(newConnection);

  const resolvedAttachmentInputs = getExistingResolvedAttachmentInputs(resolvedAttachments);
  const resolvedAttachmentForSubmit = getResolvedAttachmentsInputsForSubmit(
    false,
    resolvedAttachmentInputs,
    newConnection,
    attachmentMolfile
  );

  await updateComplexPolymer(
    complexPolymerType,
    complexPolymerId,
    complexPolymerVersionId,
    simplePolymers,
    connectionInputs,
    resolvedAttachmentForSubmit,
    offset,
    structureGraph
  );
}

function getNumMolecules(
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>,
  conjugateConnectionMap: ConjugateConnectionMap
): number {
  const conjugateIdsNotInResolvedAttachments = getUnusedIdsFromResolvedAttachments(
    resolvedAttachments,
    conjugateConnectionMap
  );
  return conjugateIdsNotInResolvedAttachments.length;
}

interface OligoConjugateComponentsSummaryProps {
  complexPolymerId: string;
  versionId: string;
  isEditable: boolean;
  oligo: OligoGraphQL;
  attachedConjugates: Array<ConjugateGraphQL>;
  simplePolymers: Array<SimplePolymerGraphQL>;
  connections: Array<ConnectionGraphQL>;
  complexPolymerName: string;
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>;
  structureGraph: PolymerStructureGraph | null;
}

export function OligoConjugateComponentsSummary({
  complexPolymerId,
  versionId,
  isEditable,
  oligo,
  attachedConjugates,
  simplePolymers,
  connections,
  complexPolymerName,
  resolvedAttachments,
  structureGraph,
}: OligoConjugateComponentsSummaryProps): JSX.Element {
  const isEditingCanvas = useSelector(
    (state: BenchlingState) => state.ui.modbio.complexPolymerVersionUIs[complexPolymerId].isEditing
  );

  const enableEdits = canEditComplexPolymer(isEditable, simplePolymers) && !isEditingCanvas;

  const numResolvedAttachments = resolvedAttachments.filter(
    (resolvedAttachment) => resolvedAttachment.chemicalStructureImage != null
  ).length;

  const conjugateConnectionMap = makeConjugateConnections(
    attachedConjugates,
    simplePolymers,
    connections,
    ComplexPolymerType.OLIGO_CONJUGATE
  );
  const numMolecules = getNumMolecules(resolvedAttachments, conjugateConnectionMap);

  return (
    <div
      className={css(styles.summary)}
      {...getTestProps({component: 'ComplexPolymerComponentsSummary', element: 'root'})}
    >
      <SectionHeader
        title="COMPONENTS"
        count={numMolecules}
        {...(enableEdits && {
          actionButton: (
            <ComplexPolymerAddConjugateMenu
              complexPolymerId={complexPolymerId}
              complexPolymerVersionId={versionId}
              complexPolymerType={ComplexPolymerType.OLIGO_CONJUGATE}
              simplePolymers={simplePolymers}
              connections={connections}
              resolvedAttachments={resolvedAttachments}
              structureGraph={structureGraph}
            />
          ),
        })}
      />
      <Row>
        <ConjugatesSummary
          complexPolymerId={complexPolymerId}
          complexPolymerVersionId={versionId}
          complexPolymerType={ComplexPolymerType.OLIGO_CONJUGATE}
          isEditable={enableEdits}
          attachedConjugates={attachedConjugates}
          resolvedAttachments={resolvedAttachments}
          simplePolymers={simplePolymers}
          conjugateConnectionMap={conjugateConnectionMap}
          complexPolymerName={complexPolymerName}
          structureGraph={structureGraph}
          onAddConnection={(connection: ConjugateConnection, attachmentMolfile: string | null) =>
            onAddConnection(
              ComplexPolymerType.OLIGO_CONJUGATE,
              complexPolymerId,
              versionId,
              simplePolymers,
              connection,
              conjugateConnectionMap,
              resolvedAttachments,
              null,
              attachmentMolfile
            )
          }
        />
      </Row>
      {numResolvedAttachments > 0 && (
        <>
          <SectionHeader title="CONJUGATES" count={numResolvedAttachments} />
          <Row>
            {resolvedAttachments.map((resolvedAttachment) => (
              <div key={resolvedAttachment.id}>
                <ResolvedAttachmentSummary resolvedAttachment={resolvedAttachment} />
              </div>
            ))}
          </Row>
        </>
      )}
      <SectionHeader
        title="BACKBONE"
        {...(enableEdits && {
          actionButton: (
            <ComplexPolymerSwapOligoMenu
              complexPolymerId={complexPolymerId}
              complexPolymerVersionId={versionId}
              complexPolymerType={ComplexPolymerType.OLIGO_CONJUGATE}
              simplePolymers={simplePolymers}
              connections={connections}
              resolvedAttachments={resolvedAttachments}
              oligo={oligo}
              structureGraph={structureGraph}
            />
          ),
        })}
      />
      <Row>
        <div
          id="oligoSummary"
          {...getTestProps({component: 'ComplexPolymerComponentsSummary', element: 'backbone'})}
        >
          <BioentityChip
            id={isInaccessible(oligo.entity) ? null : oligo.entity.id}
            size={ChipSize.MINI}
            validationExtras={null}
          />
        </div>
      </Row>
    </div>
  );
}

interface OligoDuplexComponentsSummaryProps {
  complexPolymerId: string;
  versionId: string;
  isEditable: boolean;
  forwardStrand: OligoDuplexOligoStrandGraphQL;
  reverseStrand: OligoDuplexOligoStrandGraphQL;
  offset: number;
  simplePolymers: Array<SimplePolymerGraphQL>;
  connections: Array<ConnectionGraphQL>;
  complexPolymerName: string;
  structureGraph: PolymerStructureGraph | null;
}

export function OligoDuplexComponentsSummary({
  complexPolymerId,
  versionId,
  isEditable,
  forwardStrand,
  reverseStrand,
  offset,
  simplePolymers,
  connections,
  complexPolymerName,
  structureGraph,
}: OligoDuplexComponentsSummaryProps): JSX.Element {
  const isEditingCanvas = useSelector(
    (state: BenchlingState) => state.ui.modbio.complexPolymerVersionUIs[complexPolymerId].isEditing
  );

  const enableEdits = canEditComplexPolymer(isEditable, simplePolymers) && !isEditingCanvas;

  const attachedConjugates = forwardStrand.conjugates.concat(reverseStrand.conjugates);

  const resolvedAttachments = getResolvedAttachmentsForOligoDuplex(forwardStrand, reverseStrand);
  const numResolvedAttachments = resolvedAttachments.length;

  const conjugateConnectionMap = makeConjugateConnections(
    attachedConjugates,
    simplePolymers,
    connections,
    ComplexPolymerType.OLIGO_DUPLEX
  );
  const numMolecules = getNumMolecules(resolvedAttachments, conjugateConnectionMap);

  const updateOffset = async (newOffset: number | null): Promise<void> => {
    if (newOffset == null) {
      return;
    }

    const reverseOligoPolymer = simplePolymers.find(
      (polymer) => polymer.helmReference === OLIGO_DUPLEX_REVERSE_STRAND_REF
    )!;
    const newBackboneNodes = generateBackbonePolymerStructureGraphNodes(
      ComplexPolymerType.OLIGO_DUPLEX,
      OLIGO_DUPLEX_REVERSE_STRAND_REF,
      reverseOligoPolymer.entity as SequenceEntityGraphQL,
      newOffset
    );

    const updatedStructureGraph =
      structureGraph != null
        ? {
            nodes: newBackboneNodes.concat(
              structureGraph.nodes.filter((node) => node.data.polymerRef !== OLIGO_DUPLEX_REVERSE_STRAND_REF)
            ),
            edges: structureGraph.edges,
          }
        : null;

    await withLoadingScreen(
      updateComplexPolymer(
        ComplexPolymerType.OLIGO_DUPLEX,
        complexPolymerId,
        versionId,
        simplePolymers,
        // The backend will automatically add the appropriate hydrogen bond connections.
        getConnectionInputs(
          connections.filter(
            (connection) => connection.connectionType === ComplexPolymerConnectionType.COVALENT_BOND
          )
        ),
        getExistingResolvedAttachmentInputs(resolvedAttachments),
        newOffset,
        updatedStructureGraph ?? undefined
      )
    );
  };

  return (
    <div
      className={css(styles.summary)}
      {...getTestProps({component: 'ComplexPolymerComponentsSummary', element: 'root'})}
    >
      <SectionHeader
        title="COMPONENTS"
        count={numMolecules}
        {...(enableEdits && {
          actionButton: (
            <ComplexPolymerAddConjugateMenu
              complexPolymerId={complexPolymerId}
              complexPolymerVersionId={versionId}
              complexPolymerType={ComplexPolymerType.OLIGO_DUPLEX}
              simplePolymers={simplePolymers}
              connections={connections}
              resolvedAttachments={resolvedAttachments}
              structureGraph={structureGraph}
              offset={offset}
            />
          ),
        })}
      />
      <Row>
        <ConjugatesSummary
          complexPolymerId={complexPolymerId}
          complexPolymerVersionId={versionId}
          complexPolymerType={ComplexPolymerType.OLIGO_DUPLEX}
          isEditable={enableEdits}
          attachedConjugates={attachedConjugates}
          resolvedAttachments={resolvedAttachments}
          simplePolymers={simplePolymers}
          conjugateConnectionMap={conjugateConnectionMap}
          complexPolymerName={complexPolymerName}
          structureGraph={structureGraph}
          onAddConnection={(connection: ConjugateConnection, attachmentMolfile: string | null) =>
            onAddConnection(
              ComplexPolymerType.OLIGO_DUPLEX,
              complexPolymerId,
              versionId,
              simplePolymers,
              connection,
              conjugateConnectionMap,
              resolvedAttachments,
              offset,
              attachmentMolfile
            )
          }
          offset={offset}
        />
      </Row>
      {numResolvedAttachments > 0 && (
        <>
          <SectionHeader title="CONJUGATES" count={numResolvedAttachments} />
          <Row>
            {resolvedAttachments.map((resolvedAttachment) => (
              <div key={resolvedAttachment.id}>
                <ResolvedAttachmentSummary resolvedAttachment={resolvedAttachment} />
              </div>
            ))}
          </Row>
        </>
      )}
      <SectionHeader title="BACKBONE" />
      <div className={css(styles.duplexBackboneSection)}>
        <OligoDuplexStrandSummary
          complexPolymerId={complexPolymerId}
          versionId={versionId}
          isEditable={enableEdits}
          simplePolymers={simplePolymers}
          connections={connections}
          resolvedAttachments={resolvedAttachments}
          oligo={forwardStrand.oligo}
          offset={offset}
          isForward={true}
          structureGraph={structureGraph}
        />
        <OligoDuplexOffsetField
          offset={offset}
          setOffset={updateOffset}
          forwardStrandLength={forwardStrand.oligo.bases.length}
          reverseStrandLength={reverseStrand.oligo.bases.length}
          isEditable={enableEdits}
          textBoxEditable={false}
        />
        <OligoDuplexStrandSummary
          complexPolymerId={complexPolymerId}
          versionId={versionId}
          isEditable={enableEdits}
          simplePolymers={simplePolymers}
          connections={connections}
          resolvedAttachments={resolvedAttachments}
          oligo={reverseStrand.oligo}
          offset={offset}
          isForward={false}
          structureGraph={structureGraph}
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  summary: {
    paddingLeft: 10,
    paddingBottom: 10,
    paddingRight: 10,
  },
  sectionHeader: {
    color: GRAY_SEMIDARK,
  },
  entityHeader: {
    paddingTop: 5,
  },
  conjugatesSummary: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: '8px',
    background: Color.GRAY_50,
  },
  conjugateSummary: {
    display: 'inline-block',
    padding: '4px',
    border: `1px solid ${Color.GRAY_200}`,
    borderRadius: '4px',
    margin: '4px',
    background: Color.WHITE,
  },
  conjugateSummaryHover: {
    ':hover': {
      background: Color.GRAY_200,
    },
  },
  attachmentIcons: {
    position: 'absolute',
    top: '41%',
    left: '30%',
    zIndex: 2,
  },
  connectionButton: {
    marginRight: '15px',
  },
  deletionButton: {
    background: Color.GRAY_50,
  },
  singleAttachmentIcon: {
    position: 'absolute',
    top: '41%',
    left: '45%',
    zIndex: 2,
  },
  conjugateChip: {
    marginRight: 4,
  },
  attachmentInformation: {
    display: 'inline-block',
    marginRight: 5,
  },
  chipAndAttachmentInfo: {
    display: 'flex',
  },
  conjugateImage: {
    height: 150,
    width: 150,
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  chemicalStructureImageNoRelativePosition: {
    height: '100%',
    width: '100%',
    position: 'absolute',
    zIndex: 1,
  },
  duplexBackboneSection: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    columnGap: '8px',
    rowGap: '8px',
    flexWrap: 'wrap',
    maxWidth: 800,
  },
  displayNameChip: {
    display: 'inline-block',
    pointerEvents: 'none',
  },
  conjugateIcon: {
    height: 32,
    width: 32,
    color: Color.GRAY_200,
  },
  strandChip: {
    width: 200,
    marginRight: 0,
  },
});
